// Pure math helpers for backtest visualisation. No React, no DOM, no lightweight-charts.
// Keeping the math in a separate module makes it trivially unit-testable and reusable
// by the equity curve, future Monte-Carlo viz, etc.

/**
 * Compound an array of per-trade percent returns into an equity curve starting at 100.
 * E.g. returns=[10, -5] → [100, 110, 104.5].
 * Returns one more point than the input because the curve starts at the initial 100.
 */
export function equityCurve(returns: readonly number[]): number[] {
  const out: number[] = [100];
  let equity = 100;
  for (const r of returns) {
    equity *= 1 + r / 100;
    out.push(equity);
  }
  return out;
}

/**
 * Running maximum, used to compute drawdown points alongside the equity curve.
 */
export function runningMax(values: readonly number[]): number[] {
  const out: number[] = [];
  let max = -Infinity;
  for (const v of values) {
    if (v > max) max = v;
    out.push(max);
  }
  return out;
}

/**
 * Per-trade drawdown as a negative percent: how far below the running peak each point is.
 */
export function drawdownCurve(equity: readonly number[]): number[] {
  const peaks = runningMax(equity);
  return equity.map((v, i) => (peaks[i] === 0 ? 0 : ((v - peaks[i]) / peaks[i]) * 100));
}

/** Arithmetic mean. NaN if empty. */
export function mean(xs: readonly number[]): number {
  if (xs.length === 0) return NaN;
  let s = 0;
  for (const x of xs) s += x;
  return s / xs.length;
}

/** Sample standard deviation (N-1 denominator). 0 if N<2. */
export function stdev(xs: readonly number[]): number {
  if (xs.length < 2) return 0;
  const m = mean(xs);
  let v = 0;
  for (const x of xs) {
    const d = x - m;
    v += d * d;
  }
  return Math.sqrt(v / (xs.length - 1));
}

/** Per-trade Sharpe ratio: mean / stdev. 0 when stdev is 0 (constant returns). */
export function sharpe(returns: readonly number[]): number {
  const s = stdev(returns);
  if (s === 0) return 0;
  return mean(returns) / s;
}

/**
 * Per-trade Sortino: mean / downsideStdev. Downside deviation uses raw negative returns
 * (deviation from zero, matching TradingView's convention). Returns 99 sentinel when
 * mean is positive and there are no losing trades (matches the C# implementation).
 */
export function sortino(returns: readonly number[]): number {
  if (returns.length < 2) return 0;
  const m = mean(returns);
  const negatives = returns.filter((r) => r < 0);
  if (negatives.length === 0) return m > 0 ? 99 : 0;
  let v = 0;
  for (const x of negatives) v += x * x;
  const denom = negatives.length >= 2
    ? Math.sqrt(v / (negatives.length - 1))
    : Math.sqrt(v);
  if (denom === 0) return 0;
  return m / denom;
}

/** Linear-interpolation percentile. p in [0,100]. */
export function percentile(xs: readonly number[], p: number): number {
  if (xs.length === 0) return NaN;
  const sorted = [...xs].sort((a, b) => a - b);
  if (p <= 0) return sorted[0];
  if (p >= 100) return sorted[sorted.length - 1];
  const idx = (p / 100) * (sorted.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  const frac = idx - lo;
  return sorted[lo] * (1 - frac) + sorted[hi] * frac;
}

/**
 * Bucket a series of percentage returns for histogram display. Each bucket has fixed
 * width (default 1%) and is identified by its lower edge. Empty buckets between min and
 * max are included so the histogram has no gaps.
 *
 * Returns `{ bucket, count }[]` sorted ascending by bucket lower edge.
 */
export function histogramBuckets(
  xs: readonly number[],
  bucketSize = 1,
): { bucket: number; count: number }[] {
  if (xs.length === 0) return [];
  const min = Math.min(...xs);
  const max = Math.max(...xs);
  const lo = Math.floor(min / bucketSize) * bucketSize;
  const hi = Math.floor(max / bucketSize) * bucketSize;
  const counts = new Map<number, number>();
  for (const x of xs) {
    const k = Math.floor(x / bucketSize) * bucketSize;
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  const out: { bucket: number; count: number }[] = [];
  // Round-to-2-decimals to dodge JS float weirdness like 0.1 + 0.2 = 0.30000000000000004.
  const r2 = (v: number) => Math.round(v * 100) / 100;
  for (let k = lo; k <= hi + 1e-9; k += bucketSize) {
    const key = r2(k);
    out.push({ bucket: key, count: counts.get(key) ?? 0 });
  }
  return out;
}
